import React, { useEffect, useRef, useState } from 'react';
import { StyledBenefitParentContainer, StyledCardContainer } from './styles';
import BenefitDetailsModal from '../BenefitDetailsModal';
import BenefitCard from './BenefitCard';
import ModularEnrolmentHeader from '../ModularEnrolmentHeader';
import {
    fetchModularAlaCarteBenefits,
    setModularCurrentStep,
    setModularSelectedAlaCarte
} from '../../../redux/slices/ModularEnrolmentSlice';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectedAlaCarteBenefits,
    selectModularAlaCarteBenefits,
    selectModularPolicyId,
    selectModularSelectedBasePlan
} from '../../../redux/slices/ModularEnrolmentSlice/selectors';
import { Loader } from '../../atoms';
import { Alerts, Typography } from '@loophealth/loop-ui-web-library';
import { IAlacarte } from '../../../redux/slices/PolicyListSlice/types';
import { ReduxState } from '../../../redux';
import useSegment from '../../../utils/hooks/useSegment';
import { SEGMENT_ACTIONS } from '../../../utils/constants/SegmentActionConstants';
import { ModularEnrolmentSteps } from '../../../redux/slices/ModularEnrolmentSlice/types';

const AlaCarteBenefits: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const trackClick = useSegment('click');
    const trackPage = useSegment('page');
    const alaCarteBenefits = useSelector(selectModularAlaCarteBenefits);
    const selectedBasePlan = useSelector(selectModularSelectedBasePlan);
    const selectedBenefits = useSelector(selectedAlaCarteBenefits);
    const userId = useSelector((state: ReduxState) => state.user?.userData?.data?.userId || '');
    const policyId = useSelector(selectModularPolicyId);
    const isFetched = useRef(false);

    const setSelectedBenefits = (benefitIds: string[]) => {
        dispatch(setModularSelectedAlaCarte(benefitIds));
    };

    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [focusedPlan, setFocusedPlan] = useState<IAlacarte>();
    const isLoading = alaCarteBenefits.loading;
    const errorMessage = alaCarteBenefits.error?.message;

    const benefits = alaCarteBenefits.data ?? [];

    const handleReadMore = (plan: IAlacarte) => {
        const clickAction = SEGMENT_ACTIONS.CLICK.READ_MORE;
        trackClick({
            name: clickAction.name,
            properties: {
                ...clickAction.properties,
                benefit_name: plan.name,
                benefit_id: plan.id
            }
        });
        setFocusedPlan(plan);
        setShowDetailsModal(true);
    };

    const handleUpdate = (prev: string, curr: string) => {
        const tempBenefits = selectedBenefits.filter((benefitId) => benefitId !== prev);
        setSelectedBenefits([...tempBenefits, curr]);
    };

    const handleSelect = (id: string) => {
        if (selectedBenefits.includes(id)) return;
        setSelectedBenefits([...selectedBenefits, id]);
    };

    const handleRemove = (id: string) => {
        const updatedPlans = selectedBenefits.filter((benefitId) => benefitId !== id);
        setSelectedBenefits(updatedPlans);
    };

    useEffect(() => {
        trackPage(SEGMENT_ACTIONS.PAGE.INSURANCE_BENEFITS_PAGE);
        dispatch(
            fetchModularAlaCarteBenefits({
                userId,
                policyId,
                benefitId: selectedBasePlan
            })
        );
        setTimeout(() => (isFetched.current = true), 10);
    }, []);

    useEffect(() => {
        if (!isFetched.current) return;
        if (!alaCarteBenefits.data?.length && !alaCarteBenefits.loading && !alaCarteBenefits.error) {
            dispatch(setModularCurrentStep(ModularEnrolmentSteps.SUMMARY));
        } else if (alaCarteBenefits.data?.length && !alaCarteBenefits.loading) {
            window.plotline('track', 'Viewed_insurance_benefits_page;', {
                userId,
                benefit_ids: selectedBenefits
            });
        }
    }, [alaCarteBenefits.loading, alaCarteBenefits.data, isFetched.current]);

    return (
        <StyledBenefitParentContainer>
            <ModularEnrolmentHeader
                title={'Add more to your policy'}
                subtitle={'Choose from various add-ons tailored for your needs'}
            />

            {isLoading ? (
                <Loader />
            ) : errorMessage ? (
                <Alerts.StatusAlert
                    variant="over"
                    text={
                        <Typography variant="medium" color="error">
                            {errorMessage}
                        </Typography>
                    }
                    alignItems="center"
                />
            ) : (
                <StyledCardContainer>
                    {benefits?.map((plan, i) => (
                        <BenefitCard
                            key={plan.id}
                            plan={plan}
                            selectedBenefits={selectedBenefits}
                            handleReadMore={(plan) => handleReadMore(plan)}
                            handleSelectPlan={handleSelect}
                            handleRemovePlan={handleRemove}
                            handleUpdate={handleUpdate}
                            isLastRow={i >= benefits.length - (benefits.length % 2 === 0 ? 2 : 1)}
                        />
                    ))}
                    <BenefitDetailsModal
                        benefit={focusedPlan}
                        isVisible={showDetailsModal}
                        setIsVisible={setShowDetailsModal}
                    />
                </StyledCardContainer>
            )}
        </StyledBenefitParentContainer>
    );
};

export default AlaCarteBenefits;
